import JSZip from 'jszip';
import { documentStyleIds, validateDocumentStyles, type DocumentStyle, type DocumentStyles } from '@cdf/api-zod';

/** Word documents and templates. Macro-enabled packages are refused by the content-type check. */
export const WORD_STYLE_FILE_ACCEPT = '.docx,.dotx';
export const MAX_WORD_STYLE_FILE_BYTES = 25 * 1024 * 1024;
export const MAX_DOCUMENT_STYLES = 100;
const MAX_XML_CHARACTERS = 20 * 1024 * 1024;
export const STYLE_FONTS = ['Arial', 'Calibri', 'Times New Roman', 'Georgia', 'Verdana', 'Courier New'] as const;

type StyleFont = (typeof STYLE_FONTS)[number];
export type WordStyleFormatting = Omit<DocumentStyle, 'id' | 'name'>;
export interface ImportedWordStyle {
  /** Built-in style this Word style replaces; null for a style with no equivalent here. */
  targetId: string | null;
  /** Name shown in Word. */
  wordName: string;
  formatting: WordStyleFormatting;
}
export interface FontSubstitution { word: string; used: StyleFont }
export interface WordStyleRead { styles: ImportedWordStyle[]; fontSubstitutions: FontSubstitution[] }
export interface WordStyleMerge { styles: DocumentStyles; updated: string[]; added: string[]; skipped: string[] }
export interface WordStyleDestination { action: 'update' | 'add'; name: string }

interface Accumulated {
  font?: string; size?: number; color?: string; bold?: boolean; italic?: boolean; underline?: boolean;
  alignment?: DocumentStyle['alignment']; before?: number; after?: number; line?: number; lineRule?: string;
}
const WORD_MAIN_TYPES = [
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document.main+xml',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template.main+xml',
];
const normalise = (value: string) => value.toLocaleLowerCase('en-US').replace(/[\s_-]+/g, '');
const finite = (value: string | undefined) => {
  const n = Number(value);
  return value !== undefined && value !== '' && Number.isFinite(n) ? n : undefined;
};
const clamp = (value: number, min: number, max: number, step: number) => Math.min(max, Math.max(min, Math.round(value / step) * step));

function kids(node: Element | undefined, name: string): Element[] {
  return node ? Array.from(node.children).filter(item => item.localName === name) : [];
}
function kid(node: Element | undefined, name: string): Element | undefined {
  return kids(node, name)[0];
}
/** Reads an attribute by local name so prefixed (w:val) and unprefixed forms both work. */
function attr(node: Element | undefined, name: string): string | undefined {
  if (!node) return undefined;
  for (const item of Array.from(node.attributes)) if (item.localName === name) return item.value;
  return undefined;
}
const on = (node: Element | undefined) => node ? !['0', 'false', 'off'].includes(attr(node, 'val') ?? '1') : undefined;

function parseXml(text: string, part: string): Element {
  const doc = new DOMParser().parseFromString(text, 'application/xml');
  const root = doc.documentElement;
  if (!root || root.localName === 'parsererror' || doc.getElementsByTagName('parsererror').length) throw new Error('The Word file part ' + part + ' could not be read.');
  return root;
}

class WordPackage {
  private constructor(private readonly zip: JSZip) {}
  static async open(bytes: Uint8Array) {
    try { return new WordPackage(await JSZip.loadAsync(bytes)); }
    catch { throw new Error('This file is not a Word document or template.'); }
  }
  async xml(part: string): Promise<Element> {
    const entry = this.zip.file(part);
    if (!entry) throw new Error('The Word file is missing ' + part + '.');
    const text = await entry.async('string');
    if (text.length > MAX_XML_CHARACTERS) throw new Error('The Word file is too large to read its styles.');
    return parseXml(text, part);
  }
  has(part: string) { return Boolean(this.zip.file(part)); }
  /** Relationships of a part, with targets resolved to package paths. */
  async relationships(part: string): Promise<Array<{ type: string; part: string; external: boolean }>> {
    const slash = part.lastIndexOf('/');
    const dir = slash >= 0 ? part.slice(0, slash) : '';
    const name = slash >= 0 ? part.slice(slash + 1) : part;
    const relsPart = (dir ? dir + '/' : '') + '_rels/' + name + '.rels';
    if (!this.has(relsPart)) return [];
    const root = await this.xml(relsPart);
    return kids(root, 'Relationship').map(item => {
      const target = attr(item, 'Target') ?? '';
      const external = attr(item, 'TargetMode') === 'External';
      return { type: attr(item, 'Type') ?? '', external, part: external ? target : resolvePart(dir, target) };
    });
  }
}
function resolvePart(dir: string, target: string): string {
  const segments = (target.startsWith('/') ? target.slice(1) : (dir ? dir + '/' : '') + target).split('/');
  const out: string[] = [];
  for (const segment of segments) {
    if (segment === '..') out.pop();
    else if (segment && segment !== '.') out.push(segment);
  }
  return out.join('/');
}

/** Picks the closest portable font. Exact matches are kept; anything else is reported as a substitution. */
export function portableFont(name: string): StyleFont {
  const exact = STYLE_FONTS.find(font => font.toLocaleLowerCase('en-US') === name.trim().toLocaleLowerCase('en-US'));
  if (exact) return exact;
  const n = name.toLocaleLowerCase('en-US');
  if (/mono|courier|consolas|menlo|lucida console|code/.test(n)) return 'Courier New';
  if (/verdana|tahoma|geneva/.test(n)) return 'Verdana';
  if (/georgia|cambria|garamond|palatino|book antiqua|baskerville|constantia/.test(n)) return 'Georgia';
  if (/times|serif/.test(n) && !/sans/.test(n)) return 'Times New Roman';
  if (/calibri|carlito|aptos|segoe|candara|corbel/.test(n)) return 'Calibri';
  return 'Arial';
}

async function mainPart(pkg: WordPackage): Promise<string> {
  if (!pkg.has('[Content_Types].xml')) throw new Error('This file is not a Word document or template.');
  const part = (await pkg.relationships('')).find(item => item.type.endsWith('/officeDocument') && !item.external)?.part;
  if (!part) throw new Error('This file is not a Word document or template.');
  const types = await pkg.xml('[Content_Types].xml');
  const type = kids(types, 'Override').find(item => attr(item, 'PartName') === '/' + part)?.getAttribute('ContentType');
  if (!type || !WORD_MAIN_TYPES.includes(type)) throw new Error('This file is not a Word document or template. Choose a .docx or .dotx file.');
  return part;
}

async function themeFonts(pkg: WordPackage, part: string | undefined): Promise<{ major?: string; minor?: string }> {
  if (!part || !pkg.has(part)) return {};
  const scheme = kid(kid(await pkg.xml(part), 'themeElements'), 'fontScheme');
  const face = (kind: string) => attr(kid(kid(scheme, kind), 'latin'), 'typeface') || undefined;
  return { major: face('majorFont'), minor: face('minorFont') };
}

/** Reads paragraph styles, including inherited (basedOn) and document-default formatting, from a Word file. */
export async function readWordStyles(bytes: Uint8Array): Promise<WordStyleRead> {
  const pkg = await WordPackage.open(bytes);
  const main = await mainPart(pkg);
  const rels = await pkg.relationships(main);
  const stylePart = rels.find(item => item.type.endsWith('/styles') && !item.external)?.part;
  if (!stylePart || !pkg.has(stylePart)) throw new Error('This Word file does not contain any styles.');
  const theme = await themeFonts(pkg, rels.find(item => item.type.endsWith('/theme') && !item.external)?.part);
  const root = await pkg.xml(stylePart);
  if (root.localName !== 'styles') throw new Error('The Word styles could not be read.');
  const all = kids(root, 'style');
  const definitions = new Map(all.flatMap(item => { const id = attr(item, 'styleId'); return id ? [[id, item] as const] : []; }));
  const defaults = kid(root, 'docDefaults');
  const substitutions = new Map<string, StyleFont>();

  const applyRun = (props: Element | undefined, into: Accumulated) => {
    if (!props) return;
    const fonts = kid(props, 'rFonts');
    const themed = attr(fonts, 'asciiTheme') ?? attr(fonts, 'hAnsiTheme');
    const family = themed ? (themed.startsWith('major') ? theme.major : theme.minor) : attr(fonts, 'ascii') ?? attr(fonts, 'hAnsi');
    if (family && family.length <= 80) into.font = family;
    const size = finite(attr(kid(props, 'sz'), 'val'));
    if (size !== undefined && size > 0) into.size = size / 2;
    const color = attr(kid(props, 'color'), 'val');
    if (color && /^[0-9a-f]{6}$/i.test(color)) into.color = '#' + color.toUpperCase();
    else if (color === 'auto') into.color = '#000000';
    const bold = on(kid(props, 'b')), italic = on(kid(props, 'i'));
    if (bold !== undefined) into.bold = bold;
    if (italic !== undefined) into.italic = italic;
    const underline = kid(props, 'u');
    if (underline) into.underline = (attr(underline, 'val') ?? 'single') !== 'none';
  };
  const applyParagraph = (props: Element | undefined, into: Accumulated) => {
    if (!props) return;
    const jc = attr(kid(props, 'jc'), 'val');
    if (jc) into.alignment = jc === 'center' ? 'center' : jc === 'right' || jc === 'end' ? 'right'
      : jc === 'both' || jc === 'distribute' ? 'justify' : 'left';
    const spacing = kid(props, 'spacing');
    const before = finite(attr(spacing, 'before')), after = finite(attr(spacing, 'after')), line = finite(attr(spacing, 'line'));
    if (before !== undefined) into.before = before / 20;
    if (after !== undefined) into.after = after / 20;
    if (line !== undefined && line > 0) { into.line = line; into.lineRule = attr(spacing, 'lineRule') ?? 'auto'; }
  };
  const chain = (id: string, seen = new Set<string>()): Element[] => {
    const style = definitions.get(id);
    if (!style || seen.has(id) || seen.size >= 32) return [];
    seen.add(id);
    const parent = attr(kid(style, 'basedOn'), 'val');
    return [...(parent ? chain(parent, seen) : []), style];
  };
  const formattingFor = (id: string): WordStyleFormatting => {
    const value: Accumulated = {};
    applyRun(kid(kid(defaults, 'rPrDefault'), 'rPr'), value);
    applyParagraph(kid(kid(defaults, 'pPrDefault'), 'pPr'), value);
    for (const style of chain(id)) { applyParagraph(kid(style, 'pPr'), value); applyRun(kid(style, 'rPr'), value); }
    const fontFamily = portableFont(value.font ?? 'Calibri');
    if (value.font && fontFamily.toLocaleLowerCase('en-US') !== value.font.toLocaleLowerCase('en-US')) substitutions.set(value.font, fontFamily);
    const fontSize = clamp(value.size ?? 10, 6, 96, 0.5);
    const lineHeight = value.line === undefined ? 1
      : value.lineRule === 'auto' ? value.line / 240 : value.line / 20 / fontSize;
    return {
      fontFamily, fontSize, color: value.color ?? '#000000',
      bold: value.bold ?? false, italic: value.italic ?? false, underline: value.underline ?? false,
      alignment: value.alignment ?? 'left',
      spaceBefore: clamp(value.before ?? 0, 0, 144, 0.5), spaceAfter: clamp(value.after ?? 0, 0, 144, 0.5),
      lineHeight: clamp(lineHeight, 1, 3, 0.05),
    };
  };

  const targets = new Map<string, string>([['normal', 'normal']]);
  for (let level = 1; level <= 6; level++) targets.set('heading' + level, 'heading-' + level);
  const used = new Set<string>();
  const styles: ImportedWordStyle[] = [];
  for (const style of all) {
    const id = attr(style, 'styleId');
    if (attr(style, 'type') !== 'paragraph' || !id) continue;
    const wordName = (attr(kid(style, 'name'), 'val') ?? id).trim().slice(0, 80) || id;
    const target = targets.get(normalise(wordName)) ?? targets.get(normalise(id)) ?? null;
    const custom = attr(style, 'customStyle') === '1';
    if (target) {
      if (used.has(target)) continue;
      used.add(target);
    } else if (kid(style, 'hidden') || (kid(style, 'semiHidden') && !custom) || (!custom && !kid(style, 'qFormat'))) continue;
    styles.push({ targetId: target, wordName, formatting: formattingFor(id) });
  }
  if (!styles.length) throw new Error('No paragraph styles were found in this Word file.');
  // Built-in styles first, so the 100-style limit never drops them.
  styles.sort((a, b) => Number(!a.targetId) - Number(!b.targetId));
  return { styles, fontSubstitutions: [...substitutions].map(([word, font]) => ({ word, used: font })) };
}

function findTarget(definitions: readonly DocumentStyle[], item: ImportedWordStyle): DocumentStyle | undefined {
  if (item.targetId) return definitions.find(style => style.id === item.targetId);
  const name = item.wordName.toLocaleLowerCase('en-US');
  return definitions.find(style => !documentStyleIds.some(id => id === style.id) && style.name.toLocaleLowerCase('en-US') === name);
}

/** Describes what importing one Word style would do to the current draft, for the import pick-list. */
export function wordStyleDestination(current: DocumentStyles, item: ImportedWordStyle): WordStyleDestination {
  const existing = findTarget(current.definitions, item);
  return existing ? { action: 'update', name: existing.name } : { action: 'add', name: item.wordName };
}

/**
 * Applies Word formatting to an unsaved style draft. Matching styles keep their identity and name; Word styles
 * with no match are added as new custom styles. Nothing is saved until the person applies the draft.
 */
export function mergeWordStyles(current: DocumentStyles, imported: readonly ImportedWordStyle[], newId: () => string = () => 'custom-' + crypto.randomUUID()): WordStyleMerge {
  const definitions = current.definitions.map(style => ({ ...style }));
  const updated: string[] = [], added: string[] = [], skipped: string[] = [];
  const takenNames = new Set(definitions.map(style => style.name.trim().toLocaleLowerCase('en-US')));
  for (const item of imported) {
    const existing = findTarget(definitions, item);
    if (existing) {
      Object.assign(existing, item.formatting);
      if (!updated.includes(existing.name)) updated.push(existing.name);
    } else if (definitions.length >= MAX_DOCUMENT_STYLES || takenNames.has(item.wordName.toLocaleLowerCase('en-US'))) {
      skipped.push(item.wordName);
    } else {
      definitions.push({ ...item.formatting, id: newId(), name: item.wordName });
      takenNames.add(item.wordName.toLocaleLowerCase('en-US'));
      added.push(item.wordName);
    }
  }
  try {
    return { styles: validateDocumentStyles({ ...current, definitions }), updated, added, skipped };
  } catch {
    throw new Error('The Word styles could not be combined with the current styles. Your draft is unchanged.');
  }
}
